import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { CATEGORIES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: "",
  subtitle: "",
  category: "weekly-column",
  cover_image: "",
  body: "",
  author: "",
  publish_date: "",
  published: true,
};

const field =
  "w-full bg-transparent border border-samatva-line rounded-xl px-4 py-3 text-sm font-light text-samatva-ink placeholder:text-samatva-muted/60 focus:outline-none focus:border-samatva-plum transition-colors duration-300";

export default function AdminDashboard() {
  const token = localStorage.getItem("samatva_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const logout = () => {
    localStorage.removeItem("samatva_admin_token");
    window.location.reload();
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/articles`, auth);
      setArticles(data);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Could not load articles");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });

  const startNew = () => {
    setForm(EMPTY);
    setEditing({});
  };

  const startEdit = (a) => {
    setForm({ ...EMPTY, ...a });
    setEditing(a);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) {
        await axios.put(`${API}/admin/articles/${form.id}`, form, auth);
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/articles`, form, auth);
        toast.success("Article published");
      }
      setEditing(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, auth);
      toast.success("Article deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const [uploading, setUploading] = useState(false);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, auth);
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const shown = (articles || []).filter(
    (a) => filter === "all" || a.category === filter
  );
  const catTitle = (id) => CATEGORIES.find((c) => c.id === id)?.title || id;

  return (
    <main
      className="min-h-screen bg-samatva-cream text-samatva-ink font-sans"
      data-testid="admin-dashboard"
    >
      <div className="noise-overlay" />
      <header className="border-b border-samatva-line bg-samatva-cream/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/samatva-logo.jpeg"
              alt="Samatva"
              className="w-9 h-9 rounded-full object-cover mix-blend-multiply"
            />
            <span className="font-serif tracking-[0.25em] text-lg">
              SAMATVA STUDIO
            </span>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 text-sm text-samatva-muted hover:text-samatva-plum transition-colors duration-300"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {editing ? (
          <form
            onSubmit={save}
            data-testid="article-editor-form"
            className="bg-samatva-lavender/30 border border-samatva-line rounded-[2rem] p-8 md:p-10 flex flex-col gap-6"
          >
            <h2 className="font-serif text-3xl font-light">
              {form.id ? "Edit article" : "New article"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
                  Title
                </label>
                <input
                  data-testid="article-title-input"
                  required
                  value={form.title}
                  onChange={set("title")}
                  placeholder="Article title"
                  className={field}
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
                  Subtitle
                </label>
                <input
                  data-testid="article-subtitle-input"
                  value={form.subtitle}
                  onChange={set("subtitle")}
                  placeholder="Optional subtitle"
                  className={field}
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
                  Category
                </label>
                <select
                  data-testid="article-category-select"
                  value={form.category}
                  onChange={set("category")}
                  className={`${field} cursor-pointer`}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
                  Publish date
                </label>
                <input
                  data-testid="article-date-input"
                  type="date"
                  value={form.publish_date}
                  onChange={set("publish_date")}
                  className={field}
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
                  Author
                </label>
                <input
                  data-testid="article-author-input"
                  value={form.author}
                  onChange={set("author")}
                  placeholder="Author name"
                  className={field}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
                  Cover image
                </label>
                <div className="flex gap-3 items-center">
                  <input
                    data-testid="article-cover-input"
                    value={form.cover_image}
                    onChange={set("cover_image")}
                    placeholder="https://… or upload a file"
                    className={field}
                  />
                  <label
                    data-testid="article-upload-button"
                    className={`shrink-0 px-5 py-3 rounded-full text-xs tracking-wide cursor-pointer transition-colors duration-300 ${
                      uploading
                        ? "bg-samatva-muted/40 text-samatva-cream"
                        : "bg-samatva-plum text-samatva-cream hover:bg-samatva-ink"
                    }`}
                  >
                    {uploading ? "Uploading…" : "Upload file"}
                    <input
                      data-testid="article-upload-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
                {form.cover_image && (
                  <img
                    data-testid="article-cover-preview"
                    src={form.cover_image}
                    alt="Cover preview"
                    className="mt-3 h-24 rounded-xl object-cover"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
                Body
              </label>
              <textarea
                data-testid="article-body-input"
                rows={14}
                value={form.body}
                onChange={set("body")}
                placeholder={
                  "Write the article here.\n\nSeparate paragraphs with a blank line.\n\n## Start a line like this for a heading"
                }
                className={`${field} resize-y leading-relaxed`}
              />
              <p className="text-xs text-samatva-muted mt-2">
                Blank line = new paragraph. Start a line with ## for a section
                heading.
              </p>
            </div>
            <label className="flex items-center gap-3 text-sm font-light cursor-pointer">
              <input
                data-testid="article-publish-toggle"
                type="checkbox"
                checked={form.published}
                onChange={set("published")}
                className="w-4 h-4 accent-[#7A5FA8]"
              />
              Published (visible on the website)
            </label>
            <div className="flex gap-4">
              <button
                data-testid="article-save-button"
                type="submit"
                disabled={saving}
                className="px-8 py-3 rounded-full bg-samatva-ink text-samatva-cream text-sm tracking-wide hover:bg-samatva-plum disabled:opacity-60 transition-colors duration-300 flex items-center gap-2"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                {form.id ? "Save changes" : "Publish article"}
              </button>
              <button
                data-testid="article-cancel-button"
                type="button"
                onClick={() => setEditing(null)}
                className="px-8 py-3 rounded-full border border-samatva-ink/30 text-sm tracking-wide hover:border-samatva-plum hover:text-samatva-plum transition-colors duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="font-serif text-3xl font-light">Articles</h2>
              <button
                data-testid="new-article-button"
                onClick={startNew}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-samatva-ink text-samatva-cream text-sm tracking-wide hover:bg-samatva-plum transition-colors duration-300"
              >
                <Plus size={16} /> New article
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filters">
              {[{ id: "all", title: "All" }, ...CATEGORIES].map((c) => (
                <button
                  key={c.id}
                  data-testid={`filter-${c.id}`}
                  onClick={() => setFilter(c.id)}
                  className={`px-4 py-2 rounded-full text-xs tracking-wide transition-colors duration-300 border ${
                    filter === c.id
                      ? "bg-samatva-ink text-samatva-cream border-samatva-ink"
                      : "border-samatva-line text-samatva-muted hover:border-samatva-plum hover:text-samatva-plum"
                  }`}
                >
                  {c.title}
                </button>
              ))}
            </div>

            <div data-testid="admin-article-list" className="flex flex-col gap-4">
              {articles === null && (
                <p className="text-samatva-muted font-light">Loading…</p>
              )}
              {articles !== null && shown.length === 0 && (
                <p className="text-samatva-muted font-light">
                  No articles here yet. Create your first one.
                </p>
              )}
              {shown.map((a) => (
                <div
                  key={a.id}
                  data-testid={`admin-article-row-${a.id}`}
                  className="flex items-center gap-4 bg-samatva-lavender/20 border border-samatva-line rounded-2xl p-4"
                >
                  {a.cover_image ? (
                    <img
                      src={a.cover_image}
                      alt=""
                      className="w-14 h-14 rounded-xl object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-samatva-lavender/60 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg truncate">{a.title}</p>
                    <p className="text-xs text-samatva-muted">
                      {catTitle(a.category)}
                      {a.publish_date ? ` · ${a.publish_date}` : ""} ·{" "}
                      <span
                        className={
                          a.published ? "text-samatva-plum" : "text-samatva-muted"
                        }
                      >
                        {a.published ? "Published" : "Draft"}
                      </span>
                    </p>
                  </div>
                  <button
                    data-testid={`edit-article-${a.id}`}
                    onClick={() => startEdit(a)}
                    className="p-2 rounded-full text-samatva-muted hover:text-samatva-plum hover:bg-samatva-lavender/50 transition-colors duration-300"
                    aria-label="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`delete-article-${a.id}`}
                    onClick={() => remove(a)}
                    className="p-2 rounded-full text-samatva-muted hover:text-red-500 hover:bg-red-50 transition-colors duration-300"
                    aria-label="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
